// ─────────────────────────────────────────────────────────────
// ARTI MARZIALI BAMBINI — LEZIONI PRONTE™ (versione italiana di Martial Arts Kids Class Ready™, v3) — tutti i testi e i link sono qui.
// Immagini: data/images.ts · Pixel: data/pixel.ts · Prezzi in base alla zona del visitatore (EUR / CHF): utils/currency.ts
// Copia esatta della landing inglese v3 (stessa struttura, stessa palette, stesse sezioni, stessa logica di conversione).
// Promessa (deve coincidere con annunci, checkout e PDF): piano di 52 settimane + 10 lezioni complete per bambini da 45 minuti,
// pagamento unico, soddisfatto o rimborsato per 10 giorni, i 5 bonus nell’ordine del checkout.
// Valori (EUR e CHF): libro 159; bonus 37, 27, 27, 27, 37 (155); totale 314 → 27, risparmi 287.
// Bambini + sport di contatto: nessuna promessa su autodifesa, cinture o iscrizioni; solo colpitori e aria, niente combattimento; niente testimonianze.
// Nomi dei giochi e degli esercizi = quelli del libro italiano (prompts/martial-arts-kids-class-ready-it.md).
// ─────────────────────────────────────────────────────────────
import { prices } from '../utils/currency';

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://arti-marziali-lezioni-pronte.impultienda.ar/checkout';

export const PAGE_TITLE = 'Arti Marziali Bambini — Lezioni Pronte™ | Tutto il tuo anno di lezioni per bambini, già pianificato';
export const META_DESCRIPTION =
  'Un piano di 52 settimane e 10 lezioni complete di arti marziali per bambini da 45 minuti, pronte da usare. Apri la pagina della settimana, prepara in 5 minuti e fai lezione stasera.';

// ── GLOBALE ───────────────────────────────────────────────────
export const brand = 'ARTI MARZIALI BAMBINI — LEZIONI PRONTE™';
/** Testo di tutti i pulsanti che scendono fino alla sezione dell’offerta */
export const ctaLabel = 'PIANIFICA TUTTO IL MIO ANNO';
export const ctaMicrocopy = `PDF subito · Pagamento unico di ${prices.main} · Soddisfatto o rimborsato per 10 giorni`;
export const stickyLabel = `PIANIFICA IL MIO ANNO · ${prices.main}`;
export const topBarMobileLabel = `IL LIBRO · ${prices.main}`;
export const price = prices.main;

// ── 1. HERO (a chi è rivolto, titolo, sottotitolo breve, mockup, pulsante) ──
export const hero = {
  eyebrow: 'PER ISTRUTTORI DI KARATE, TAEKWONDO, KICKBOXING E MUAY THAI',
  titleLead: 'TUTTO IL TUO ANNO DI LEZIONI PER BAMBINI,',
  titleHighlight: 'GIÀ PIANIFICATO.',
  sub: 'Piano di 52 settimane + 10 lezioni complete per bambini da 45 minuti, pronte da usare. Apri la pagina della settimana e fai lezione stasera.',
  mockupAlt: 'Arti Marziali Bambini — Lezioni Pronte™ sullo schermo di un computer, con i 5 bonus su tablet e smartphone',
};

// ── 2. NUMERI ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'SETTIMANE PIANIFICATE' },
  { value: '10', label: 'LEZIONI COMPLETE' },
  { value: '45', label: 'MINUTI, GIÀ CRONOMETRATI' },
  { value: '3', label: 'FASCE D’ETÀ IN OGNI LEZIONE' },
];

// ── 2b. PRIMA / DOPO ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'BASTA PREPARARE LA LEZIONE LA SERA PRIMA.',
  before: {
    label: 'PRIMA',
    when: 'La sera prima',
    quote: '«Cosa faccio fare ai bambini stasera?»',
    items: ['A cercare esercizi nei video.', 'Sempre lo stesso esercizio di pugni.', 'I piccoli persi, i grandi annoiati.'],
  },
  after: {
    label: 'DOPO',
    when: 'Sul tatami',
    items: ['Apri la pagina della settimana.', 'Prepara in 5 minuti.', 'Fai la lezione da 45 minuti, già cronometrata.', 'Saluto finale e invia la nota per i genitori.'],
  },
};

// ── 3. DI COSA HA BISOGNO IL TUO CORSO QUESTA SETTIMANA (scegli un obiettivo, guarda la lezione pronta) ──
const block =
  'Struttura della lezione: saluto e gioco di riscaldamento 8 · tecnica 12 · esercizi ai colpitori e in coppia 12 · gioco finale 8 · defaticamento e valore della settimana 5';
export const selector = {
  eyebrow: 'PARTI DAL LIVELLO DEI TUOI ALLIEVI',
  title: 'DI COSA HA BISOGNO IL TUO CORSO QUESTA SETTIMANA?',
  sub: 'Scegli un obiettivo e guarda la lezione già pronta.',
  startLabel: 'LA TUA PROSSIMA LEZIONE',
  tablistLabel: 'Obiettivi del corso',
  columns: ['PAGINA 1 · PIANO DELLA LEZIONE', 'PAGINA 2 · COME SPIEGARLA', 'PAGINA 3 · ESERCIZI E GIOCO'],
  note: 'Tutte le 10 lezioni seguono questa stessa struttura in 3 pagine. Adatta ognuna al tuo stile e alle regole di sicurezza della tua palestra.',
  items: [
    {
      tab: 'Un buon inizio: guardia e rispetto',
      n: 'LEZIONE 01 · SETTIMANA 1',
      title: 'Guardia, posizione e rispetto del dojo',
      goal: 'Il bambino fa il saluto iniziale, sta in una posizione di guardia solida con le mani alte e, al comando, torna in posizione di pronti.',
      open: [
        'Obiettivo della lezione, attrezzatura e preparazione in 5 minuti',
        'Le tue parole di apertura: «Piedi divaricati, un piede indietro. Mani su, vicino al viso. Questa è la vostra guardia!»',
        block,
      ],
      activity: [
        '5 passaggi tecnici con i punti chiave: posizione di saluto, saluto, posizione di pronti, posizione di guardia, guardia',
        '3 correzioni rapide, come «Piedi sui binari, non sul filo!»',
        'Scheda fascia d’età: come insegnarla a 4–6, 7–9 e 10–12 anni',
      ],
      close: [
        '2 esercizi con lo schema del tatami: «Controllo della guardia» e «Saltella e statua!»',
        'Gioco finale: «Il dojo dice»',
        'Momento del valore (Rispetto), nota per i genitori e verifica delle tacche',
      ],
    },
    {
      tab: 'Calci con equilibrio',
      n: 'LEZIONE 03 · SETTIMANA 9',
      title: 'Calcio frontale ed equilibrio',
      goal: 'Il bambino alza il ginocchio, calcia in avanti con l’avampiede e rimette il piede a terra in posizione, con controllo.',
      open: [
        'Obiettivo della lezione, attrezzatura e preparazione in 5 minuti',
        'Le tue parole di apertura: «Ginocchio su… calcio… ginocchio indietro… piede giù. La guardia resta alta!»',
        block,
      ],
      activity: [
        'Passaggi tecnici con i punti chiave, dal ginocchio su al piede giù',
        '3 correzioni rapide per gli errori più frequenti nei calci',
        'Scheda fascia d’età per 4–6, 7–9 e 10–12 anni',
      ],
      close: [
        '2 esercizi allo scudo da calci, con schema del tatami e tempi',
        'Gioco finale: «Calci al semaforo»',
        'Momento del valore (Autocontrollo), nota per i genitori e verifica delle tacche',
      ],
    },
    {
      tab: 'Combinazioni ai colpitori',
      n: 'LEZIONE 06 · SETTIMANA 22',
      title: 'Combinazioni di pugni ai colpitori',
      goal: 'Il bambino tira una combinazione di 3 pugni (jab, diretto, gancio) sul colpitore e torna in guardia.',
      open: [
        'Obiettivo della lezione, attrezzatura e preparazione in 5 minuti',
        'Le tue parole di apertura: «1 è il jab, 2 è il diretto, 3 è il gancio. Pronti? Uno-due-tre… guardia!»',
        block,
      ],
      activity: [
        'Passaggi tecnici per il gancio e per l’uno-due-tre',
        'Come i più grandi tengono i colpitori al compagno, in sicurezza',
        'Scheda fascia d’età per 4–6, 7–9 e 10–12 anni',
      ],
      close: [
        '2 esercizi ai colpitori con schema del tatami, serie e tempi',
        'Gioco finale: «La combinazione che cresce»',
        'Momento del valore (Spirito di squadra), nota per i genitori e verifica delle tacche',
      ],
    },
    {
      tab: 'Sicurezza e liberazioni dalle prese',
      n: 'LEZIONE 09 · SETTIMANA 35',
      title: 'Liberarsi dalle prese e sicurezza con gli sconosciuti',
      goal: 'Il bambino si libera da una presa leggera al polso, fa un passo indietro, usa la voce forte e sa che deve correre da un adulto di fiducia e raccontarglielo.',
      open: [
        'Obiettivo della lezione, attrezzatura e preparazione in 5 minuti',
        'Le tue parole di apertura: liberati, passo indietro, voce forte, corri da un adulto di fiducia',
        block,
      ],
      activity: [
        'Passaggi semplici per liberarsi, provati piano e con delicatezza',
        'Le abitudini di sicurezza: resta con il tuo gruppo, usa la voce, dillo a un adulto di fiducia',
        'Scheda fascia d’età per 4–6, 7–9 e 10–12 anni',
      ],
      close: [
        '2 giochi di ruolo tranquilli con schema del tatami',
        'Gioco finale: «Staffetta della voce forte»',
        'Momento del valore (Sicurezza), nota per i genitori e verifica delle tacche',
      ],
    },
  ],
};

// ── 4. SFOGLIA IL LIBRO (pagine vere, nello stesso ordine di pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'PAGINE VERE. GUARDA IL FORMATO PRIMA DI COMPRARE.',
  title: 'ECCO COSA AVRAI ACCANTO AL TATAMI.',
  sub: 'Apri una pagina qualsiasi e leggila a tutto schermo: il tuo piano di 52 settimane, le 3 pagine di ogni lezione, la prova generale dell’esame di cintura e la tua scheda di pianificazione.',
  hint: 'Scorri per vedere le altre · Tocca una pagina per ingrandirla',
  note: 'Sono pagine scelte, non il libro completo.',
  captions: [
    'Il tuo piano di 52 settimane (3° trimestre)',
    'Lezione 01 · piano della lezione',
    'Lezione 07 · come spiegarla',
    'Lezione 05 · esercizi e gioco',
    'Lezione 10 · scheda dell’esame di prova',
    'Scheda di pianificazione + registro delle tacche',
  ],
};

// ── 5. IL SISTEMA ──────────────────────────────
export const plan = {
  eyebrow: 'UN SOLO SISTEMA PER OGNI LEZIONE',
  title: 'TUTTO L’ANNO È GIÀ PIANIFICATO. TU DEVI SOLO SALUTARE E INSEGNARE.',
  text: 'Ogni settimana dell’anno ha già il suo posto: l’inizio di una delle 10 lezioni complete, un gioco o un esercizio da 10 minuti già pronto, oppure una verifica delle tacche. Ogni lezione ti dà il piano, i passaggi tecnici, gli esercizi e il gioco in 3 pagine.',
  steps: [
    { title: 'APRI', text: 'Trova la settimana nel tuo piano di 52 settimane.' },
    { title: 'PREPARA', text: 'Leggi la pagina e prepara il tatami in 5 minuti.' },
    { title: 'INSEGNA', text: 'Gioco di riscaldamento, una tecnica, colpitori e lavoro in coppia.' },
    { title: 'SALUTO FINALE', text: 'Gioco finale, momento del valore e nota per i genitori.' },
  ],
  featuresTitle: 'IN OGNI LEZIONE COMPLETA',
  features: [
    { icon: 'goal', title: 'Un obiettivo chiaro per la lezione', text: 'Una frase su cosa faranno i bambini, la settimana in cui iniziarla e cosa devono già saper fare.' },
    {
      icon: 'timer',
      title: 'Una lezione da 45 minuti, già cronometrata',
      text: 'Saluto e gioco di riscaldamento 8 · Tecnica 12 · Esercizi ai colpitori e in coppia 12 · Gioco finale 8 · Defaticamento e valore della settimana 5.',
    },
    { icon: 'steps', title: 'Passaggi tecnici + correzioni rapide', text: 'Passaggi numerati con i punti chiave e la frase esatta per correggere gli errori più frequenti.' },
    { icon: 'questions', title: '4–6, 7–9 e 10–12 anni', text: 'Come insegnare la stessa lezione a tutte le età sul tuo tatami, anche in un gruppo misto.' },
    { icon: 'quote', title: 'Le tue parole, parola per parola', text: 'Parole di apertura, comandi e momento del valore, più un consiglio da esperto e un errore da evitare.' },
    { icon: 'action', title: 'Esercizi, gioco + nota per i genitori', text: '2 esercizi e un gioco finale con lo schema del tatami, una verifica delle tacche e una nota per i genitori.' },
  ],
} as const;

// ── 5b. VALORI + FASCE D’ETÀ ──────────────────
export const valuesBand = {
  eyebrow: 'MOLTO PIÙ DI CALCI E PUGNI',
  title: 'UN VALORE PER OGNI LEZIONE. PER TUTTO L’ANNO.',
  sub: 'Ogni lezione si chiude con un breve momento del valore, parola per parola, così i genitori vedono cosa si portano a casa i loro figli.',
  values: [
    { n: '01', value: 'Rispetto' },
    { n: '02', value: 'Concentrazione' },
    { n: '03', value: 'Autocontrollo' },
    { n: '04', value: 'Perseveranza' },
    { n: '05', value: 'Fiducia in se stessi' },
    { n: '06', value: 'Spirito di squadra' },
    { n: '07', value: 'Attenzione' },
    { n: '08', value: 'Coraggio' },
    { n: '09', value: 'Sicurezza' },
    { n: '10', value: 'Disciplina' },
  ],
  agesTitle: 'OGNI LEZIONE SI ADATTA AI BAMBINI CHE HAI DAVANTI',
  ages: [
    { age: '4–6 ANNI', text: 'Segnaposto a terra, nomi di animali, passaggi brevi detti ad alta voce e tanti giochi.' },
    { age: '7–9 ANNI', text: 'La tecnica completa sui colpitori dell’istruttore, cambiando lato al comando.' },
    { age: '10–12 ANNI', text: 'Più velocità, spostamenti con la guardia alta, tenere i colpitori al compagno e aiutare a guidare il gruppo.' },
  ],
};

// ── 6. IL PIANO DI 52 SETTIMANE (stesse settimane e stessi nomi dei giochi del libro italiano) ──
export const yearPlan = {
  eyebrow: 'IL TUO PIANO DI 52 SETTIMANE',
  title: 'TUTTO L’ANNO, SETTIMANA PER SETTIMANA.',
  sub: 'Le settimane ★ iniziano una nuova lezione. Le settimane ◆ sono verifiche delle tacche. Nelle settimane in mezzo hai un gioco o un esercizio da 10 minuti già pronto.',
  quarters: [
    {
      q: 'T1',
      weeks: 'SETTIMANE 1–13',
      name: 'COSTRUIRE LE BASI',
      full: [
        { week: '1', label: 'INIZIA LA LEZIONE 01', title: 'Guardia, posizione e rispetto del dojo' },
        { week: '5', label: 'INIZIA LA LEZIONE 02', title: 'Pugni dritti: jab e diretto' },
        { week: '9', label: 'INIZIA LA LEZIONE 03', title: 'Calcio frontale ed equilibrio' },
        { week: '13', label: 'VERIFICA DELLE TACCHE', title: 'Controlla i progressi con la scheda', review: true },
      ],
      activities: '+ giochi ed esercizi da 10 minuti già pronti, come «Statua in guardia», «Tutti in fila!» e «Il fenicottero».',
    },
    {
      q: 'T2',
      weeks: 'SETTIMANE 14–26',
      name: 'COLPIRE E DIFENDERSI',
      full: [
        { week: '14', label: 'INIZIA LA LEZIONE 04', title: 'Calcio circolare' },
        { week: '18', label: 'INIZIA LA LEZIONE 05', title: 'Parate: alta, media e bassa' },
        { week: '22', label: 'INIZIA LA LEZIONE 06', title: 'Combinazioni di pugni ai colpitori' },
        { week: '26', label: 'VERIFICA DELLE TACCHE', title: 'Controlla i progressi con la scheda', review: true },
      ],
      activities: '+ giochi ed esercizi da 10 minuti già pronti, come «L’orologio», «Para la pioggia» e «Combinazioni a numeri».',
    },
    {
      q: 'T3',
      weeks: 'SETTIMANE 27–39',
      name: 'MUOVERSI E STARE AL SICURO',
      full: [
        { week: '27', label: 'INIZIA LA LEZIONE 07', title: 'Spostamenti e distanza' },
        { week: '31', label: 'INIZIA LA LEZIONE 08', title: 'Cadere in sicurezza' },
        { week: '35', label: 'INIZIA LA LEZIONE 09', title: 'Liberarsi dalle prese e sicurezza con gli sconosciuti' },
        { week: '39', label: 'VERIFICA DELLE TACCHE', title: 'Controlla i progressi con la scheda', review: true },
      ],
      activities: '+ giochi ed esercizi da 10 minuti già pronti, come «Troppo vicino, troppo lontano», «La sedia a dondolo» e «Zona sicura».',
    },
    {
      q: 'T4',
      weeks: 'SETTIMANE 40–52',
      name: 'METTERE TUTTO INSIEME',
      full: [
        { week: '40', label: 'INIZIA LA LEZIONE 10', title: 'Prova generale dell’esame di cintura' },
        { week: '52', label: 'VERIFICA DELLE TACCHE', title: 'Verifica delle tacche di fine anno', review: true },
      ],
      activities: '+ giochi ed esercizi da 10 minuti già pronti, come «Campione delle parate», «Sotto i riflettori» e «Staffetta del dojo».',
    },
  ],
  toolsTitle: 'IN PIÙ, I TUOI STRUMENTI DA ISTRUTTORE',
  tools: [
    'Quando la lezione si complica: 8 situazioni, cosa dire e cosa fare',
    'Scheda di pianificazione + registro delle tacche: pianifica qualsiasi lezione in 5 minuti',
    'Scheda da tatami: il promemoria da tenere a bordo tatami',
  ],
};

// ── 7. LE 10 LEZIONI COMPLETE ─────────────────────────────────
export const meetings = {
  eyebrow: 'LE 10 LEZIONI COMPLETE',
  title: 'DIECI LEZIONI PER BAMBINI. PRONTE DA USARE.',
  sub: 'Segui il piano di 52 settimane, oppure inizia dalla lezione di cui i tuoi allievi hanno bisogno adesso. Ognuna occupa 3 pagine: piano della lezione, come spiegarla, esercizi e gioco.',
  weekLabel: 'INIZIO: SETTIMANA',
  items: [
    { n: '01', week: '1', title: 'Guardia, posizione e rispetto del dojo', goal: 'Saluto iniziale, una posizione di guardia solida con le mani alte e posizione di pronti al comando.' },
    { n: '02', week: '5', title: 'Pugni dritti: jab e diretto', goal: 'Jab e diretto partendo dalla guardia, riportando ogni volta la mano al viso.' },
    { n: '03', week: '9', title: 'Calcio frontale ed equilibrio', goal: 'Ginocchio su, calcio con l’avampiede e piede a terra in posizione, con controllo.' },
    { n: '04', week: '14', title: 'Calcio circolare', goal: 'Ruotare il piede d’appoggio, ginocchio di lato e calciare un bersaglio basso o medio con controllo.' },
    { n: '05', week: '18', title: 'Parate: alta, media e bassa', goal: 'Tre parate al comando, con gli occhi sull’istruttore.' },
    { n: '06', week: '22', title: 'Combinazioni di pugni ai colpitori', goal: 'Una combinazione di 3 pugni (jab, diretto, gancio) sul colpitore e ritorno in guardia.' },
    { n: '07', week: '27', title: 'Spostamenti e distanza', goal: 'Avanzare, indietreggiare e spostarsi di lato senza incrociare i piedi, e trovare la distanza giusta.' },
    { n: '08', week: '31', title: 'Cadere in sicurezza', goal: 'Cadere all’indietro e di lato da una posizione bassa, con il mento al petto, e rialzarsi in sicurezza.' },
    { n: '09', week: '35', title: 'Liberarsi dalle prese e sicurezza con gli sconosciuti', goal: 'Liberarsi da una presa leggera al polso, usare la voce forte e correre da un adulto di fiducia.' },
    { n: '10', week: '40', title: 'Prova generale dell’esame di cintura', goal: 'Un esame di cintura di prova, in tutta calma, con le tecniche dell’anno, una scheda di valutazione e il testo della cerimonia.' },
  ],
  cta: 'VOGLIO LE 10 LEZIONI + 5 BONUS',
  ctaMicrocopy: `${prices.main} · Pagamento unico`,
};

// ── 8. BONUS (stesso ordine e stessi valori del checkout) ──────────────
export const bonuses = {
  eyebrow: `5 BONUS INCLUSI NEL TUO LIBRO DA ${prices.main}`,
  title: 'TUTTO IL RESTO CHE SERVE ALLE TUE LEZIONI PER BAMBINI.',
  sub: 'Giochi, esami di cintura, messaggi per i genitori, esercizi ai colpitori e una lezione di prova, pronti da usare.',
  valueLabel: 'VALORE',
  free: 'IN REGALO',
  totalNote: `${prices.bonusTotal} di bonus, inclusi in regalo`,
  items: [
    {
      label: 'BONUS 01',
      value: prices.bonuses[0],
      short: '60 giochi di arti marziali per bambini',
      hook: 'Non resterai mai più senza giochi.',
      title: '60 giochi di arti marziali per bambini — senza attrezzatura',
      text: '60 giochi divertenti e sicuri per aprire, dare energia e chiudere qualsiasi lezione, divisi per obiettivo: riscaldamento, reazione, equilibrio, concentrazione, calci e pugni, spirito di squadra e defaticamento. Ognuno con preparazione, regole, durata e un consiglio di sicurezza.',
    },
    {
      label: 'BONUS 02',
      value: prices.bonuses[1],
      short: 'Kit esame di cintura',
      hook: 'Esami di cintura tranquilli e ben organizzati.',
      title: 'Kit esame di cintura — requisiti, scaletta della giornata e diplomi da stampare',
      text: 'Una lista di requisiti di esempio per ogni cintura (da adattare al tuo stile), una scheda di valutazione, la scaletta della giornata d’esame con i tempi, cosa dire per aprire e chiudere l’esame, un registro delle tacche e diplomi da stampare.',
    },
    {
      label: 'BONUS 03',
      value: prices.bonuses[2],
      short: 'Kit comunicazione con i genitori',
      hook: 'Sai sempre cosa mandare alle famiglie.',
      title: 'Kit comunicazione con i genitori — lettera di benvenuto, regole del dojo e messaggi pronti',
      text: 'Una lettera di benvenuto per i nuovi allievi, le regole del dojo per bambini in una pagina, la lista di cosa portare, gli avvisi per gli esami di cintura, una nota mensile sui valori, messaggi per le assenze e frasi pronte per parlare con calma con i genitori.',
    },
    {
      label: 'BONUS 04',
      value: prices.bonuses[3],
      short: '50 esercizi ai colpitori e in coppia',
      hook: 'Lavoro ai colpitori sempre nuovo, in ogni lezione.',
      title: '50 esercizi ai colpitori e in coppia — pronti per ogni lezione per bambini',
      text: '50 esercizi con colpitori, scudi da calci e compagni per pugni, calci, parate, combinazioni, spostamenti e reazione. Ognuno con preparazione, serie e tempi, come tenere il colpitore e una nota di sicurezza.',
    },
    {
      label: 'BONUS 05',
      value: prices.bonuses[4],
      short: 'La guida alla lezione di prova',
      hook: 'Fai in modo che ogni prima lezione lasci il segno.',
      title: 'La guida alla lezione di prova — una prima lezione che bambini e genitori ricorderanno',
      text: 'Come accogliere la famiglia all’ingresso, la scaletta di una lezione di prova di 30 minuti con giochi e una prima tecnica che ogni bambino riesce a fare, cosa dire alla fine e messaggi pronti da inviare nei giorni successivi.',
    },
  ],
};

// ── 9. OFFERTA ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'LA TUA PROSSIMA LEZIONE INIZIA QUI',
  title: 'TUTTO IL TUO ANNO DI LEZIONI PER BAMBINI. IN UN SOLO LIBRO.',
  kicker: 'IL LIBRO DIGITALE COMPLETO',
  name: 'Arti Marziali Bambini — Lezioni Pronte™',
  description:
    'Un piano di 52 settimane e 10 lezioni complete per bambini da 45 minuti, pronte da usare, più cinque bonus: giochi, un kit per l’esame di cintura, messaggi per i genitori, esercizi ai colpitori e una guida alla lezione di prova.',
  mockupAlt: 'Tutto quello che è incluso in Arti Marziali Bambini — Lezioni Pronte™',
  includedTitle: 'IL TUO LIBRO CONTIENE',
  included: [
    'Piano di 52 settimane: tutto l’anno pianificato',
    '10 lezioni complete per bambini, 3 pagine ciascuna',
    'Una struttura da 45 minuti, già cronometrata, in ogni lezione',
    'Passaggi tecnici, punti chiave e correzioni rapide',
    '4–6, 7–9 e 10–12 anni in ogni lezione',
    'Esercizi, gioco finale, momento del valore e nota per i genitori',
    'Strumenti da istruttore: momenti difficili, scheda di pianificazione, scheda da tatami',
  ],
  /** Riepilogo del valore: esattamente i prezzi del checkout */
  stackTitle: 'TUTTO QUELLO CHE RICEVI',
  mainItem: { label: 'Arti Marziali Bambini — Lezioni Pronte™ (piano di 52 settimane + 10 lezioni complete)', value: prices.anchor },
  totalLabel: 'Valore totale',
  totalValue: prices.totalValue,
  todayLabel: 'OGGI',
  priceNote: `Pagamento unico · Circa ${prices.perWeek} a settimana`,
  saveNote: `Risparmi ${prices.savings}`,
  /** L’UNICO pulsante che porta al checkout */
  cta: `SÌ, VOGLIO IL LIBRO — ${prices.main}`,
  ctaNote: ['Vai al pagamento sicuro', 'Accesso inviato via email dopo il pagamento'],
  format:
    'PDF in italiano. Consegna digitale. Adatta ogni lezione al tuo stile, alle regole di sicurezza della tua palestra e all’età e al livello dei tuoi allievi.',
  guaranteeNote: 'Soddisfatto o rimborsato per 10 giorni',
};

// ── 10. GARANZIA ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10 GIORNI',
  sealBottom: 'DI GARANZIA',
  title: 'SODDISFATTO O RIMBORSATO PER 10 GIORNI. SENZA BISOGNO DI SPIEGAZIONI.',
  text: 'Usalo nelle tue prossime lezioni. Se non fa per te, scrivi a support@stockdigital.shop entro 10 giorni dall’acquisto e ti restituiamo i soldi.',
};

// ── 11. DOMANDE FREQUENTI ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'SAPPI ESATTAMENTE COSA RICEVI',
  title: 'QUALCHE DOMANDA PRIMA DI SALIRE SUL TATAMI.',
  sub: 'Pensato per istruttori e titolari di palestre che insegnano ai bambini. Un libro di lezioni pronte da usare, non un videocorso.',
  items: [
    {
      q: 'Che cos’è, esattamente?',
      a: 'Un libro in PDF in italiano: un piano di 52 settimane (ogni settimana ha l’inizio di una lezione, un gioco o un esercizio da 10 minuti già pronto, oppure una verifica delle tacche) e 10 lezioni complete per bambini da 45 minuti, di 3 pagine ciascuna, più gli strumenti per l’istruttore e 5 bonus.',
    },
    {
      q: 'Per quali stili funziona?',
      a: 'Per gli stili di percussione insegnati ai bambini: karate, taekwondo, kickboxing, muay thai e programmi misti. Le tecniche hanno nomi generici (calcio frontale, jab, parata alta) e ogni lezione ti ricorda di usare i nomi, l’etichetta e l’ordine delle cinture del tuo stile.',
    },
    {
      q: 'Per quali età è pensato?',
      a: 'Per bambini dai 4 ai 12 anni circa. Ogni lezione ha una scheda fascia d’età che spiega come insegnarla a 4–6, 7–9 e 10–12 anni, quindi funziona anche con gruppi di età miste.',
    },
    {
      q: 'C’è combattimento o contatto?',
      a: 'Niente combattimento libero. I bambini colpiscono colpitori, bersagli e l’aria. Gli esercizi in coppia si fanno con contatto leggero e controllato, solo sui colpitori e sempre sotto supervisione, e le cadute si insegnano sul tatami partendo da posizioni basse.',
    },
    {
      q: 'La lezione sulla sicurezza insegna l’autodifesa?',
      a: 'La lezione 09 insegna liberazioni dalle prese semplici e delicate e abitudini di sicurezza: restare con il proprio gruppo, usare la voce forte e dirlo a un adulto di fiducia. Non è una promessa che un bambino sappia difendersi in qualsiasi situazione.',
    },
    {
      q: 'Come lo ricevo?',
      a: 'Quando il pagamento viene approvato, l’accesso arriva all’indirizzo email che inserisci al momento del pagamento. Non viene spedito niente. Usa un indirizzo email a cui hai accesso e, se serve, controlla anche la cartella spam.',
    },
    {
      q: 'Posso usare le lezioni una per una?',
      a: 'Sì. Ogni lezione funziona da sola: puoi iniziare da quella di cui i tuoi allievi hanno bisogno adesso, oppure seguire il piano di 52 settimane dalla settimana 1.',
    },
    {
      q: 'Posso stamparlo?',
      a: 'Sì. Le pagine si leggono bene sul telefono o sul tablet accanto al tatami, e la scheda di pianificazione e il registro delle tacche sono pronti da stampare.',
    },
    {
      q: 'Posso condividerlo con i miei istruttori?',
      a: 'Questo acquisto è per il tuo uso personale come istruttore. Condividerlo tra più palestre, ridistribuirlo o rivenderlo non è incluso in questa offerta.',
    },
    { q: 'È un abbonamento?', a: `No. Un solo pagamento di ${prices.main}.` },
    { q: 'Come funziona la garanzia?', a: 'Soddisfatto o rimborsato per 10 giorni: hai 10 giorni per chiedere il rimborso, senza bisogno di spiegazioni.' },
  ],
};

// ── 12. ULTIMA CHIAMATA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'BASTA CON «COSA FACCIO FARE AI BAMBINI STASERA?»',
  title: 'SALI SUL TATAMI CON LA LEZIONE DI STASERA GIÀ PRONTA.',
  text: 'Apri la pagina della settimana. Prepara in 5 minuti. Fai una lezione a cui i bambini vorranno tornare.',
  microcopy: `Piano di 52 settimane + 10 lezioni complete + 5 bonus · ${prices.main}, pagamento unico`,
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'ARTI MARZIALI BAMBINI — LEZIONI PRONTE™ · Materiale didattico per istruttori. Segui sempre le regole del tuo stile e le norme di sicurezza della tua palestra.',
  supportLabel: 'Assistenza:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
